import { EProjectType } from '../../../enum/project';
import { ProjectImages } from '../../../types/UnifiedPdfImageTypes';
import { IButtonPanelProps, IUnifiedPdfProps, IContainerYardImages, IPavedImages, IAirportImagesType, IRailwayImagesType } from '../types';
import { logWarn, LogTag } from '../../../utils/utils';

/**
 * Helper function to extract project-specific images from unified ProjectImages
 * Maps unified ProjectImages to specific PDF component requirements
 */
export const getProjectTypeImages = (projectType: EProjectType, projectImages: ProjectImages | null): IContainerYardImages | IPavedImages | IAirportImagesType | IRailwayImagesType | null => {
    if (!projectImages) return null;

    switch (projectType) {
        case EProjectType.container_yard:
        case EProjectType.parking_deck:
            return {
                tireImg1: projectImages.tireImg1 || '',
                tireImg2: projectImages.tireImg2 || '',
                tireImg3: projectImages.tireImg3 || '',
                chartImg: projectImages.chartImg || '',
            };

        case EProjectType.paved:
            return {
                tireImageUnreinforced: projectImages.tireImageUnreinforced || '',
                tireImageReinforced: projectImages.tireImageReinforced || '',
                tireImageGeogrid: projectImages.tireImageGeogrid || '',
                chartImg: projectImages.chartImg || '',
                // 🔧 FIX: Include dynamic design images for custom design canvas
                surfaceDesignImages: projectImages.surfaceDesignImages,
                baseDesignImages: projectImages.baseDesignImages,
                subbaseDesignImages: projectImages.subbaseDesignImages,
            } as IPavedImages;

        case EProjectType.airport:
            return {
                tireImageUnstabilized: projectImages.tireImageUnstabilized || '',
                tireImageIncreasedPassages: projectImages.tireImageIncreasedPassages || '',
                tireImageGeogrid: projectImages.tireImageGeogrid || '',
                chartImg: projectImages.chartImg || '',
            };

        case EProjectType.railways:
            return {
                tireImageUnreinforced: projectImages.tireImageUnreinforced || '',
                tireImageReinforced: projectImages.tireImageReinforced || '',
            };

        case EProjectType.unpaved:
            return {
                tireImg1: projectImages.tireImg1 || '',
                tireImg2: projectImages.tireImg2 || '',
                tireImg3: projectImages.tireImg3 || '',
                chartImg: projectImages.chartImg || '',
            };

        default:
            logWarn('[propMappers]', `Unknown project type for image mapping: ${projectType}`, undefined, LogTag.PDF_EXPORT);
            return null;
    }
};

/**
 * Effective props interface for internal processing (simplified for unified mode only)
 */
export interface IEffectiveProps {
    projectType: EProjectType | null;
    mappedImages?: IContainerYardImages | IPavedImages | IAirportImagesType | IRailwayImagesType | null;
    isGeneratingPdfImages?: boolean;
    pdfImageError?: Error | null;
    geoGridLayersImage?: string;
    isGeneratingGeoGridPdfImages?: boolean;
    geoGridPdfError?: Error | null;
}

/**
 * Get effective image props based on unified or legacy mode
 */
export const getEffectiveImageProps = (props: IButtonPanelProps): IEffectiveProps => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case
    const unifiedProps = props as IUnifiedPdfProps & IButtonPanelProps;
    const projectType = unifiedProps.projectType;

    const mappedImages = getProjectTypeImages(projectType, unifiedProps.projectImages || null);

    return {
        projectType,
        mappedImages,
        isGeneratingPdfImages: unifiedProps.isGeneratingPdfImages || false,
        pdfImageError: unifiedProps.pdfImageError || null,
        geoGridLayersImage: unifiedProps.geoGridLayersImage,
        isGeneratingGeoGridPdfImages: unifiedProps.isGeneratingGeoGridPdfImages || false,
        geoGridPdfError: unifiedProps.geoGridPdfError || null,
    };
};

/**
 * Get project-specific image data based on current mode (unified or legacy)
 */
export const getProjectImageData = (projectType: EProjectType, effectiveProps: IEffectiveProps): IContainerYardImages | IPavedImages | IAirportImagesType | IRailwayImagesType | null => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case
    return effectiveProps.mappedImages || null;
};

/**
 * Get current generating state based on mode and project type
 */
export const getIsGeneratingImages = (projectType: EProjectType, effectiveProps: IEffectiveProps): boolean => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case
    return effectiveProps.isGeneratingPdfImages || false;
};

/**
 * Get current error state based on mode and project type
 */
export const getPdfError = (projectType: EProjectType, effectiveProps: IEffectiveProps): Error | null => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case
    return effectiveProps.pdfImageError || null;
};

/**
 * GeoGrid props interface
 */
export interface IGeoGridProps {
    geoGridLayersImage?: string;
    isGeneratingGeoGridPdfImages: boolean;
    geoGridPdfError?: Error | null;
}

/**
 * Get GeoGrid specific props
 */
export const getGeoGridProps = (effectiveProps: IEffectiveProps): IGeoGridProps => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case
    return {
        geoGridLayersImage: effectiveProps.geoGridLayersImage,
        isGeneratingGeoGridPdfImages: effectiveProps.isGeneratingGeoGridPdfImages || false,
        geoGridPdfError: effectiveProps.geoGridPdfError,
    };
};

/**
 * Combined PDF props interface
 */
export interface ICombinedPdfHelperProps {
    isGeneratingCombinedPdfImages: boolean;
    combinedPdfError?: Error | null;
}

/**
 * Get Combined PDF specific props
 */
export const getCombinedPdfProps = (effectiveProps: IEffectiveProps, geoGridProps: IGeoGridProps): ICombinedPdfHelperProps => {
    // Since both DesignCondition and GeogridDesign now use unified props,
    // this function only needs to handle the unified case - combine states
    const isGeneratingImages = effectiveProps.isGeneratingPdfImages || false;
    const pdfError = effectiveProps.pdfImageError;

    return {
        isGeneratingCombinedPdfImages: isGeneratingImages || geoGridProps.isGeneratingGeoGridPdfImages,
        combinedPdfError: pdfError || geoGridProps.geoGridPdfError,
    };
};
